import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../UI';
import Sidebar from './Sidebar';
import MobileNavBar from '../Mobile/MobileNavBar';
import { Menu, User, LogOut, Key, CreditCard, Code } from 'lucide-react';

const AppLayout = ({ children, onMenuAction, pageTitle = "패스워드", pageSubtitle, stats }) => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // 데스크톱에서는 기본 열림, 모바일에서는 기본 닫힘
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.innerWidth > 768;
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // 모바일로 전환 시 사이드바 자동 닫기
      if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // 사이드바 토글
  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    localStorage.setItem('sidebarOpen', JSON.stringify(newState));
  };

  // 사이드바 닫기 (모바일용)
  const closeSidebar = () => {
    setSidebarOpen(false);
    localStorage.setItem('sidebarOpen', JSON.stringify(false));
  };

  // 로그아웃 핸들러
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('로그아웃 에러:', error);
      alert('로그아웃 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="app-layout">
      {/* 사이드바 */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        onClose={closeSidebar}
        onMenuAction={onMenuAction}
      />

      {/* 메인 콘텐츠 영역 */}
      <div className={`main-content ${sidebarOpen ? 'content-shifted' : ''}`}>
        {/* 상단 헤더 */}
        <header className="app-header">
          <div className="header-left">
            {/* 모바일 메뉴 버튼 */}
            <button
              className="mobile-menu-button"
              onClick={toggleSidebar}
              title="메뉴 열기"
            >
              <Menu size={20} />
            </button>
            
            {/* 페이지 타이틀 */}
            <div className="page-title-section">
              <div className="page-title-row">
                <h1 className="page-title">
                  {pageTitle}
                </h1>
                {/* 통계 배지 */}
                {stats && (
                  <div className="stats-inline">
                    <div className="stat-badge stat-password" title={`패스워드 ${stats.passwords || 0}개`}>
                      <Key size={12} />
                      <span>{stats.passwords || 0}</span>
                    </div>
                    <div className="stat-badge stat-creditcard" title={`신용카드 ${stats.creditCards || 0}개`}>
                      <CreditCard size={12} />
                      <span>{stats.creditCards || 0}</span>
                    </div>
                    <div className="stat-badge stat-apikey" title={`API 키 ${stats.apiKeys || 0}개`}>
                      <Code size={12} />
                      <span>{stats.apiKeys || 0}</span>
                    </div>
                  </div>
                )}
              </div>
              {pageSubtitle && (
                <p className="page-subtitle-header">
                  {pageSubtitle}
                </p>
              )}
            </div>
          </div>

          <div className="header-right">
            {/* 사용자 정보 */}
            <div className="user-info">
              <span className="user-name">
                {user?.displayName || user?.email}
              </span>
              <User size={16} />
            </div>

            {/* 로그아웃 버튼 */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="logout-button"
              title="로그아웃"
            >
              <LogOut size={16} />
              <span className="logout-text">로그아웃</span>
            </Button>
          </div>
        </header>

        {/* 페이지 콘텐츠 */}
        <main className="page-content">
          {children}
        </main>
      </div>
      
      {/* Mobile Navigation Bar */}
      {isMobile && <MobileNavBar />}
    </div>
  );
};

export default AppLayout;